import React, { useEffect, useState } from 'react';

import GeneralRiskImpactCategoriesTab from '../../radio/framework/impact_categories/GeneralRiskImpactCategoriesTab';
import GeneralRiskStatusesTab from '../../radio/framework/statuses/GeneralRiskStatusesTab';
import GeneralRiskTypesTab from '../../radio/framework/types/GeneralRiskTypesTab';

import GhostButton from './GhostButton';
import SetupCard from './SetupCard';
import StepShell from './StepShell';

import { useToast } from 'Hooks/useToast';

const LISTS = [
    { key: 'types', title: () => $t('Risk types'), Tab: GeneralRiskTypesTab },
    { key: 'impact_categories', title: () => $t('Impact categories'), Tab: GeneralRiskImpactCategoriesTab },
    { key: 'statuses', title: () => $t('Status categories'), Tab: GeneralRiskStatusesTab },
];

const regionId = (key) => `setup-wizard-radio-${key}`;

/**
 * R.A.D.I.O. Opening the page offers the starter set once per event: the
 * server fills only the lists that are still empty and reports which ones, so
 * those carry a "Starter set loaded" chip. The lists are the existing general
 * risk framework tabs, editing through the wizard's own aliases.
 */
export default function RadioStep({ step, urls, deferring, saving, onDefer, onNext, onRefresh }) {
    const [starterLists, setStarterLists] = useState(null);
    // The three lists open together from the one "Edit categories" button.
    const [editing, setEditing] = useState(false);
    const toast = useToast();

    // Status follows whether risk types and impact categories exist, so any
    // change in a list re-reads the rail.
    const refreshRail = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    useEffect(() => {
        axios
            .post(urls.starter)
            .then(({ data }) => setStarterLists(data.starter_lists ?? []))
            .catch(() => {
                // The lists are still usable without the starter set.
                setStarterLists([]);
                toast.error($t('Something went wrong. Please try again.'));
            });
    }, [urls.starter]);

    return (
        <StepShell
            step={step}
            lede={$t(
                'Risks, assumptions, decisions, issues and opportunities. A starter set of categories is offered by default; adjust it to your framework.',
            )}
            deferring={deferring}
            saving={saving}
            onDefer={onDefer}
            onNext={onNext}
        >
            {starterLists === null ? (
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            ) : (
                <div className="tw-flex tw-flex-col tw-gap-y-2">
                    {LISTS.map(({ key, title, Tab }) => (
                        <SetupCard
                            key={key}
                            heading={title()}
                            chip={starterLists.includes(key) ? $t('Starter set loaded') : null}
                            expanded={editing}
                            regionId={regionId(key)}
                        >
                            <Tab
                                fetchUrl={urls[key].index}
                                storeUrl={urls[key].store}
                                updateUrl={urls[key].update}
                                deleteUrl={urls[key].destroy}
                                onChanged={refreshRail}
                            />
                        </SetupCard>
                    ))}
                    <div className="tw-mt-1.5">
                        <GhostButton
                            expanded={editing}
                            aria-controls={LISTS.map(({ key }) => regionId(key)).join(' ')}
                            onClick={() => setEditing((current) => !current)}
                        >
                            {$t('Edit categories')}
                        </GhostButton>
                    </div>
                </div>
            )}
        </StepShell>
    );
}
